import { effect, inject, Injectable, signal } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    BroadcastChannelEvent,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { GeomFilter } from '@connect-services/client/services/layer.service';
import { Layer, LayerControllerGetRequestParams, LayersService } from '@connect-services/src/sdk/api';

export const EDIT_SESSION_BY_PROJECT = 'editSessionByProject';

export type EditSessionByProject = {
    id: string;
    startDate: string;
};

@Injectable({
    providedIn: 'root',
})
export class ProjectEditModeService {
    public button: HTMLButtonElement = document.createElement('button');
    public readonly editableLayerIds = signal<string[]>([]);
    public readonly geomNoEditableLayerIdsWithReason = signal<{ id: string; reasonForUnavailability?: string }[]>([]);
    public readonly isEditModeActive = signal<boolean>(false);

    private readonly broadcastChannel: BroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.EDIT_MODE);
    private readonly preserveEntityDetailPanelRequested = signal<boolean>(false);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly fetchAllService = inject(FetchAllService);
    private readonly layersService = inject(LayersService);
    private readonly projectBloc = inject(ProjectBloc);

    public constructor() {
        effect(async () => {
            const projectId = this.projectBloc.value()?.id;
            if (!this.isEditModeActive() || !projectId) {
                return;
            }

            this.editableLayerIds.set((await this.fetchEditableLayers(projectId)).map((layer: Layer) => layer.id));
            this.geomNoEditableLayerIdsWithReason.set(await this.fetchGeomNoEditableLayers(projectId));
        });

        effect(() => {
            if (!this.projectBloc.value()) {
                return;
            }

            const editSessionInfo: EditSessionByProject | null = this.getEditSession();
            if (!editSessionInfo) {
                return;
            }

            this.isEditModeActive.set(true);
            this.toggleEditModeButtonActiveState(true);
        });

        effect(() => {
            this.broadcastChannel.postMessage(<BroadcastChannelEvent<{ isEditModeActive: boolean }>>{
                additionalData: { isEditModeActive: this.isEditModeActive() },
                source: BroadcastChannelSourceEnum.HOMEPAGE,
            });
        });
    }

    public activateEditMode(): void {
        if (this.isEditModeActive() || !this.projectBloc.value()) {
            return;
        }

        const existingInfo = localStorage.getItem(EDIT_SESSION_BY_PROJECT);
        const newProjectSessionInfo: EditSessionByProject = { id: uuidv4(), startDate: new Date().toISOString() };
        this.isEditModeActive.set(true);

        if (!existingInfo) {
            localStorage.setItem(
                EDIT_SESSION_BY_PROJECT,
                JSON.stringify({
                    [this.projectBloc.value()!.id]: newProjectSessionInfo,
                }),
            );

            return;
        }

        const existingInfoParsed: Record<string, EditSessionByProject> = JSON.parse(existingInfo);

        if (existingInfoParsed[this.projectBloc.value()!.id]) {
            return;
        }

        existingInfoParsed[this.projectBloc.value()!.id] = newProjectSessionInfo;
        localStorage.setItem(EDIT_SESSION_BY_PROJECT, JSON.stringify(existingInfoParsed));
    }

    public consumePreserveEntityDetailPanel(): boolean {
        const wasPreserveRequested = this.preserveEntityDetailPanelRequested();

        this.preserveEntityDetailPanelRequested.set(false);

        return wasPreserveRequested;
    }

    public deactivateEditMode(): void {
        this.clearEditSession();
        this.isEditModeActive.set(false);
    }

    public getEditSession(): EditSessionByProject | null {
        const editSessionByProject = localStorage.getItem(EDIT_SESSION_BY_PROJECT);

        return JSON.parse(editSessionByProject ?? 'null')?.[this.projectBloc.value()!.id];
    }

    public preserveEntityDetailPanel(): void {
        this.preserveEntityDetailPanelRequested.set(true);
    }

    public refreshEditSessionStart(): void {
        const editSessionByProjectParsed = this.getEditSessionByProject();
        if (!editSessionByProjectParsed) {
            return;
        }

        editSessionByProjectParsed[this.projectBloc.value()!.id].startDate = new Date().toISOString();
        localStorage.setItem(EDIT_SESSION_BY_PROJECT, JSON.stringify(editSessionByProjectParsed));
    }

    private clearEditSession(): void {
        const editSessionByProjectParsed = this.getEditSessionByProject();
        if (!editSessionByProjectParsed) {
            return;
        }

        delete editSessionByProjectParsed[this.projectBloc.value()!.id];
        if (!Object.keys(editSessionByProjectParsed).length) {
            localStorage.removeItem(EDIT_SESSION_BY_PROJECT);

            return;
        }

        localStorage.setItem(EDIT_SESSION_BY_PROJECT, JSON.stringify(editSessionByProjectParsed));
    }

    private fetchEditableLayers(projectId: string): Promise<Layer[]> {
        return this.fetchAllService.getAll<LayerControllerGetRequestParams, Layer>(
            this.layersService.layerControllerGet.bind(this.layersService),
            { geomFilter: GeomFilter.ALL, onlyEditableLayer: true, project: projectId, types: [Layer.TypeEnum.TABLE] },
        );
    }

    private fetchGeomNoEditableLayers(projectId: string): Promise<{ id: string; reasonForUnavailability?: string }[]> {
        return this.fetchAllService.getAll<
            LayerControllerGetRequestParams,
            { id: string; reasonForUnavailability?: string }
        >(this.layersService.layerControllerGetGeomNoEditable.bind(this.layersService), {
            project: projectId,
            types: [Layer.TypeEnum.TABLE],
        });
    }

    private getEditSessionByProject(): Record<string, EditSessionByProject> | null {
        if (!this.projectBloc.value()) {
            return null;
        }

        const editSessionByProject = localStorage.getItem(EDIT_SESSION_BY_PROJECT);
        if (!editSessionByProject) {
            return null;
        }

        const editSessionByProjectParsed: Record<string, EditSessionByProject> = JSON.parse(editSessionByProject);
        if (!editSessionByProjectParsed[this.projectBloc.value()!.id]) {
            return null;
        }

        return editSessionByProjectParsed;
    }

    private toggleEditModeButtonActiveState(isActive: boolean): void {
        this.button.classList.toggle('is-active', isActive);
    }
}
